import {useCallback, useMemo, useState} from 'react'
import {useTranslation} from 'i18n'
import {requestData} from 'services'
import {GiftShopInvoiceDetail} from 'types'
import {showSnackbar} from 'utils/components'
import {useDebounce} from 'utils/debounces'

export function useTransactionDetailRateContentHooks() {
  const stateCreatorToRate = useState<{
    username: string
    creator_id: string
    photo: string
    status: string
    invoice_id: string
  }>()
  const [isRatedLocal, setIsRatedLocal] = useState<Record<string, boolean>>({})
  const setCreatorToRate = stateCreatorToRate[1]
  const stateSubmitLoading = useState(false)
  const setRateSubmitLoading = stateSubmitLoading[1]
  const debounce = useDebounce()
  const {translate} = useTranslation()

  const handleRateCreator = useCallback(
    (item: GiftShopInvoiceDetail) => {
      const {
        creator_photo,
        creator_status,
        creator_name,
        invoice_id: itemInvoiceId,
        creator_id,
        content,
      } = item
      if (!creator_photo || !creator_status || !creator_id) return
      if (content.some((dt) => dt.quality !== 'hi-res')) return

      setCreatorToRate({
        username: creator_name,
        creator_id,
        photo: creator_photo,
        invoice_id: itemInvoiceId,
        status: creator_status,
      })
    },
    [setCreatorToRate],
  )

  const handlePostRating = useCallback(
    (creator_id: string, related_invoice_id: string, ratingInput: number) => {
      setRateSubmitLoading(true)
      debounce(() => {
        requestData('giftshop_post_creator_rating', {
          actionType: 'execute',
          data: {
            creator_id,
            invoice_id: related_invoice_id,
            rating: ratingInput,
          },
          onRequestReceived: () => setRateSubmitLoading(false),
          onRequestSuccess: ({status: responseStatus}) => {
            if (responseStatus === 200) {
              showSnackbar(translate('messages:creatorRatingSubmitSuccess'))
              setCreatorToRate(undefined)
              setIsRatedLocal((prev) => ({
                ...prev,
                [related_invoice_id]: true,
              }))
            } else {
              showSnackbar(translate('messages:creatorRatingSubmitFailed'))
            }
          },
        })
      })
    },
    [debounce, setCreatorToRate, setRateSubmitLoading, translate],
  )

  return useMemo(
    () => ({
      isRatedLocal,
      stateCreatorToRate,
      stateSubmitLoading,
      handlePostRating,
      handleRateCreator,
    }),
    [
      handlePostRating,
      handleRateCreator,
      isRatedLocal,
      stateCreatorToRate,
      stateSubmitLoading,
    ],
  )
}
